//* 정산 내역 페이지 전용 hook (미정산 + 정산완료)
//* 기존 shopOrder.hook.ts의 useShopOrderListAgencyAdminHookHook 대체

import { fetcher, queryString, useRecoilQuery } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';

import {
  monthlyAccountedDataAtom,
  monthlyAccountedStatusAtom,
  needAccountDataAtom,
  needAccountStatusAtom,
} from './needAccount.atom';
import { AccountDateType, AccountDetailType } from './needAccount.interface';

/**
 * need-account URL 생성
 * @param bugoRole - 유저 역할
 * @param dateType - 날짜 필터 기준 (purchasedAt: 판매일, coffinOutDate: 발인일)
 */
export const needAccountUrl = (bugoRole: string, dateType?: AccountDateType) => {
  return `/${bugoRole}/shop-order/need-account?${queryString({
    ...(dateType && { dateType }),
  })}`;
};

/**
 * account-detail/export URL 생성 (세부내역 모달 + CSV 다운로드용)
 * 전체 데이터를 페이지네이션 없이 반환
 * @param bugoRole - 유저 역할
 * @param year - 조회 연도
 * @param month - 조회 월 (1~12)
 * @param type - 조회 유형 (need: 미정산, accounted: 정산완료)
 * @param dateType - 날짜 필터 기준
 */
export const accountDetailExportUrl = (
  bugoRole: string,
  year: number,
  month: number,
  type: AccountDetailType,
  dateType?: AccountDateType,
) => {
  return `/${bugoRole}/shop-order/account-detail/export?${queryString({
    year,
    month,
    type,
    ...(dateType && { dateType }),
  })}`;
};

/**
 * account-detail URL 생성 (페이지네이션 조회용, 필요시 사용)
 */
export const accountDetailUrl = (
  bugoRole: string,
  year: number,
  month: number,
  type: AccountDetailType,
  dateType?: AccountDateType,
  page?: number,
  limit?: number,
) => {
  return `/${bugoRole}/shop-order/account-detail?${queryString({
    year,
    month,
    type,
    ...(dateType && { dateType }),
    ...(page && { page }),
    ...(limit && { limit }),
  })}`;
};

/**
 * 미정산 내역 조회 hook
 * - need-account 엔드포인트로 월별 그룹핑된 데이터 조회
 * - dateType으로 판매일/발인일 기준 전환
 * @param dateType - 날짜 필터 기준 (purchasedAt: 판매일, coffinOutDate: 발인일)
 */
export const useNeedAccountHook = (dateType?: AccountDateType) => {
  const { bugoRole } = useAuth();

  useRecoilQuery(
    needAccountStatusAtom,
    needAccountDataAtom,
    needAccountUrl(bugoRole(), dateType),
    fetcher,
  );
};

// ── 정산완료 내역 (MonthlyAccounted) ──

/**
 * monthly-accounted URL 생성
 * @param bugoRole - 유저 역할
 * @param year - 조회 연도
 * @param dateType - 날짜 필터 기준 (purchasedAt: 판매일, coffinOutDate: 발인일)
 */
export const monthlyAccountedUrl = (
  bugoRole: string,
  year?: number,
  dateType?: AccountDateType,
) => {
  return `/${bugoRole}/shop-order/monthly-accounted?${queryString({
    ...(year && { year }),
    ...(dateType && { dateType }),
  })}`;
};

/**
 * 정산완료 내역 조회 hook
 * - monthly-accounted 엔드포인트로 연간 월별 그룹핑된 데이터 조회
 * @param year - 조회 연도 (미지정 시 백엔드 기본값: 현재 KST 연도)
 * @param dateType - 날짜 필터 기준 (purchasedAt: 판매일, coffinOutDate: 발인일)
 */
export const useMonthlyAccountedHook = (year?: number, dateType?: AccountDateType) => {
  const { bugoRole } = useAuth();

  useRecoilQuery(
    monthlyAccountedStatusAtom,
    monthlyAccountedDataAtom,
    monthlyAccountedUrl(bugoRole(), year, dateType),
    fetcher,
  );
};
